import React from "react";
import { Box, Code, Table as ChakraTable, Stack, Text } from "@chakra-ui/react";
import { Observation } from "./types";

const ScopeUsageTable: React.FC<{ data: { scope: string; count: number }[] }> = ({
  data,
}) => (
  <ChakraTable.Root size="sm" striped variant="line">
    <ChakraTable.Header>
      <ChakraTable.Row bg="bg.subtle">
        <ChakraTable.ColumnHeader>
          <Text fontWeight="bold">Identifier Scope</Text>
        </ChakraTable.ColumnHeader>
        <ChakraTable.ColumnHeader>
          <Text fontWeight="bold">Number of Identifiers</Text>
        </ChakraTable.ColumnHeader>
      </ChakraTable.Row>
    </ChakraTable.Header>
    <ChakraTable.Body>
      {data.map((row) => (
        <ChakraTable.Row key={row.scope}>
          <ChakraTable.Cell>
            <Text fontWeight="bold">{row.scope}</Text>
          </ChakraTable.Cell>
          <ChakraTable.Cell>{row.count}</ChakraTable.Cell>
        </ChakraTable.Row>
      ))}
    </ChakraTable.Body>
  </ChakraTable.Root>
);

interface UnresolvedRef {
  pointer: string;
  identifier?: { scope?: string; value?: string };
}

const UnresolvedReferencesTable: React.FC<{ data: UnresolvedRef[] }> = ({
  data,
}) => (
  <ChakraTable.Root size="sm" striped variant="line">
    <ChakraTable.Header>
      <ChakraTable.Row bg="bg.subtle">
        <ChakraTable.ColumnHeader>
          <Text fontWeight="bold">JSON Pointer</Text>
        </ChakraTable.ColumnHeader>
        <ChakraTable.ColumnHeader>
          <Text fontWeight="bold">Scope</Text>
        </ChakraTable.ColumnHeader>
        <ChakraTable.ColumnHeader>
          <Text fontWeight="bold">Value</Text>
        </ChakraTable.ColumnHeader>
      </ChakraTable.Row>
    </ChakraTable.Header>
    <ChakraTable.Body>
      {data.map((row, i) => (
        <ChakraTable.Row key={`${row.pointer}-${i}`}>
          <ChakraTable.Cell>
            <Code size="sm">{row.pointer}</Code>
          </ChakraTable.Cell>
          <ChakraTable.Cell>{row.identifier?.scope ?? ""}</ChakraTable.Cell>
          <ChakraTable.Cell>{row.identifier?.value ?? ""}</ChakraTable.Cell>
        </ChakraTable.Row>
      ))}
    </ChakraTable.Body>
  </ChakraTable.Root>
);

const GenericPayload: React.FC<{ data: unknown }> = ({ data }) => (
  <Box
    as="pre"
    fontFamily="mono"
    fontSize="sm"
    bg="bg.subtle"
    padding="8px 12px"
    borderRadius="md"
    overflowX="auto"
    whiteSpace="pre-wrap"
  >
    {JSON.stringify(data, null, 2)}
  </Box>
);

export const ObservationView: React.FC<{ observation: Observation }> = ({
  observation,
}) => {
  let body: React.ReactNode;
  switch (observation.kind) {
    case "scopeUsage":
      body = (
        <ScopeUsageTable
          data={
            (observation.data as { scope: string; count: number }[]) ?? []
          }
        />
      );
      break;
    case "unresolvedReferences":
      body = (
        <UnresolvedReferencesTable
          data={(observation.data as UnresolvedRef[]) ?? []}
        />
      );
      break;
    default:
      body = <GenericPayload data={observation.data} />;
  }

  return (
    <Stack gap="10px">
      <Text fontSize="sm" color="fg.muted">
        {observation.message}
      </Text>
      {body}
    </Stack>
  );
};

export const ObservationList: React.FC<{ observations: Observation[] }> = ({
  observations,
}) => {
  if (observations.length === 0) {
    return <Text color="fg.muted">No observations for this selection.</Text>;
  }
  return (
    <Stack gap="16px">
      {observations.map((obs) => (
        <ObservationView key={obs.id} observation={obs} />
      ))}
    </Stack>
  );
};
